import { useCallback, useState } from 'react';
import type { CommandAction, CommandPayload, CommandResult } from '@glyph/protocol-ts';
import type { CmdOptions } from '../client/types';
import { CommandError } from '../client/types';
import { useClient, useDeviceId } from './provider';

export interface CommandState {
  pending: Partial<Record<CommandAction, boolean>>;
  lastError: CommandError | null;
}

/** Fire a device command; tracks per-action pending state and the last error for toasts. */
export function useCommand(id?: string) {
  const client = useClient();
  const deviceId = useDeviceId(id);
  const [state, setState] = useState<CommandState>({ pending: {}, lastError: null });

  const run = useCallback(
    async <A extends CommandAction>(action: A, payload: CommandPayload<A>, opts?: CmdOptions): Promise<CommandResult<A> | undefined> => {
      setState((s) => ({ ...s, pending: { ...s.pending, [action]: true }, lastError: null }));
      try {
        return await client.cmd(deviceId, action, payload, opts);
      } catch (e) {
        const err = e instanceof CommandError ? e : new CommandError((e as Error).message, 'unknown');
        setState((s) => ({ ...s, lastError: err }));
        return undefined;
      } finally {
        setState((s) => ({ ...s, pending: { ...s.pending, [action]: false } }));
      }
    },
    [client, deviceId],
  );

  return { run, pending: state.pending, lastError: state.lastError, clearError: () => setState((s) => ({ ...s, lastError: null })) };
}
